import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Building, Copy, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SubOrganizationCredits from "@/components/sub-organizations/SubOrganizationCredits";
import { ShowcaseQRCode } from "@/components/organization/ShowcaseQRCode";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import {
  SubOrganization,
  SubOrgRole,
  buildSubOrgInviteLink,
  useArchiveSubOrganization,
  useCancelSubOrgInvite,
  useInviteSubOrgUser,
  useMainOrgCameras,
  useParentAllocations,
  useRemoveSubOrgUser,
  useSetDevicePublic,
  useSetSubOrgDevice,
  useSubOrgContext,
  useSubOrgDevices,
  useSubOrgUsers,
  useSubOrganization,
  useSubOrganizations,
  useUpdateSubOrganization,
  useUpdateSubOrgUserRole,
} from "@/hooks/use-sub-organizations";

const PROFILE_FIELDS: { key: keyof SubOrganization; label: string; long?: boolean }[] = [
  { key: "name", label: "Organisation name" },
  { key: "website", label: "Website" },
  { key: "industry", label: "Industry" },
  { key: "address", label: "Address", long: true },
  { key: "country", label: "Country" },
  { key: "description", label: "Description", long: true },
  { key: "opening_times", label: "Opening times" },
  { key: "logo_url", label: "Logo image link" },
  { key: "marketing_banner_url", label: "Banner image link" },
  { key: "image_1_url", label: "Image 1 link" },
  { key: "image_2_url", label: "Image 2 link" },
  { key: "image_3_url", label: "Image 3 link" },
];

const ProfileTab = ({ org, canManage }: { org: SubOrganization; canManage: boolean }) => {
  const update = useUpdateSubOrganization();
  const [form, setForm] = useState<Record<string, string>>({});
  useEffect(() => {
    const f: Record<string, string> = {};
    PROFILE_FIELDS.forEach(({ key }) => (f[key] = (org[key] as string) ?? ""));
    setForm(f);
  }, [org]);

  const save = () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    const data: Record<string, string | null> = {};
    PROFILE_FIELDS.forEach(({ key }) => (data[key] = form[key]?.trim() || null));
    data.name = form.name.trim();
    update.mutate({ id: org.id, data: data as any });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Shown on the sub-customer's portal and showcase page.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          {PROFILE_FIELDS.map(({ key, label, long }) => (
            <div key={key} className={long ? "md:col-span-2 space-y-2" : "space-y-2"}>
              <Label htmlFor={`f-${key}`}>{label}</Label>
              {long ? (
                <Textarea
                  id={`f-${key}`}
                  value={form[key] ?? ""}
                  disabled={!canManage}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              ) : (
                <Input
                  id={`f-${key}`}
                  value={form[key] ?? ""}
                  disabled={!canManage}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
        {canManage && (
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save profile
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const UsersTab = ({ org, canManage }: { org: SubOrganization; canManage: boolean }) => {
  const { data, isLoading } = useSubOrgUsers(org.id);
  const invite = useInviteSubOrgUser();
  const cancel = useCancelSubOrgInvite();
  const remove = useRemoveSubOrgUser();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SubOrgRole>("standard");

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error("Enter a valid email address");
    await invite.mutateAsync({ subOrg: org, email, role });
    setEmail("");
  };

  const copy = (id: string, mail: string) => {
    navigator.clipboard.writeText(buildSubOrgInviteLink(id, mail));
    toast.success("Invitation link copied");
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Invite a user</CardTitle>
            <CardDescription>They'll get access to all cameras shared with this sub-customer. No camera picking needed.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row gap-3">
            <Input placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="md:max-w-sm" />
            <Select value={role} onValueChange={(v) => setRole(v as SubOrgRole)}>
              <SelectTrigger className="md:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (view)</SelectItem>
                <SelectItem value="editor">Editor (can edit)</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={send} disabled={invite.isPending}>
              {invite.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send invitation
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Access</TableHead>
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.users || []).map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell>{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                    <TableCell>{u.email || "—"}</TableCell>
                    <TableCell>
                      {canManage ? (
                        <RoleSelect subOrgId={org.id} userId={u.user_id} role={u.role as SubOrgRole} />
                      ) : (
                        <Badge variant="secondary">{u.role === "editor" ? "Editor" : "Standard"}</Badge>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm("Remove this user from the sub-customer? They will lose access to its cameras."))
                              remove.mutate({ subOrgId: org.id, userId: u.user_id });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {(data?.invites || []).map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>
                      <Badge variant="outline">Invited</Badge>
                    </TableCell>
                    <TableCell>{i.email}</TableCell>
                    <TableCell>{i.invited_role === "editor" ? "Editor" : "Standard"}</TableCell>
                    {canManage && (
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => copy(i.id, i.email)}>
                          <Copy className="h-4 w-4 mr-1" /> Copy link
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => cancel.mutate(i.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!data?.users.length && !data?.invites.length && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground">
                      No users yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const RoleSelect = ({ subOrgId, userId, role }: { subOrgId: string; userId: string; role: SubOrgRole }) => {
  const update = useUpdateSubOrgUserRole();
  return (
    <Select value={role} onValueChange={(v) => update.mutate({ subOrgId, userId, role: v as SubOrgRole })}>
      <SelectTrigger className="w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="standard">Standard</SelectItem>
        <SelectItem value="editor">Editor</SelectItem>
      </SelectContent>
    </Select>
  );
};

const CamerasTab = ({ org, canManage }: { org: SubOrganization; canManage: boolean }) => {
  const { data: cameras = [], isLoading } = useMainOrgCameras(org.parent_organization_id);
  const { data: allocated = [] } = useSubOrgDevices(org.id);
  const { data: siblings = [] } = useSubOrganizations(org.parent_organization_id);
  const { data: allAlloc = [] } = useParentAllocations(siblings.map((s) => s.id));
  const setDevice = useSetSubOrgDevice();
  const [search, setSearch] = useState("");

  const allocatedIds = useMemo(() => new Set(allocated.map((a) => a.device_id)), [allocated]);
  const setPublic = useSetDevicePublic(org.parent_organization_id);
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const canSetPublic = canManage && canEditDeviceConfig;
  const pendingPublicId = setPublic.isPending ? setPublic.variables?.deviceId : undefined;
  const sharedWith = (deviceId: string) =>
    allAlloc
      .filter((a) => a.device_id === deviceId && a.sub_organization_id !== org.id)
      .map((a) => siblings.find((s) => s.id === a.sub_organization_id)?.name)
      .filter(Boolean);

  const shown = cameras.filter((c) => {
    if (!canManage && !allocatedIds.has(c.device_id)) return false;
    const q = search.toLowerCase();
    return !q || [c.device_name, c.device_serial, c.device_id].some((v) => v?.toLowerCase().includes(q));
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cameras</CardTitle>
        <CardDescription>
          {allocatedIds.size} shared with this sub-customer. Cameras stay owned by the main organisation; credits and
          billing are unchanged. A camera can be shared with several sub-customers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input placeholder="Search by name, serial or ID" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Shared</TableHead>
                <TableHead className="w-12">Public</TableHead>
                <TableHead>Camera</TableHead>
                <TableHead>Serial</TableHead>
                <TableHead>Also shared with</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.device_id}>
                  <TableCell>
                    <Checkbox
                      checked={allocatedIds.has(c.device_id)}
                      disabled={!canManage || setDevice.isPending || !!org.archived_at}
                      onCheckedChange={(v) => setDevice.mutate({ subOrgId: org.id, deviceId: c.device_id, allocate: !!v })}
                      aria-label={`Share ${c.device_name || c.device_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    {pendingPublicId === c.device_id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <Checkbox
                        checked={!!c.enable_public_view}
                        disabled={!canSetPublic || setPublic.isPending}
                        onCheckedChange={(v) => setPublic.mutate({ deviceId: c.device_id, isPublic: !!v })}
                        aria-label={`Make ${c.device_name || c.device_id} public`}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {c.device_name || c.device_id}
                    {c.enable_public_view && !allocatedIds.has(c.device_id) && (
                      <div className="text-xs text-muted-foreground">Public, but not shared with this sub-customer</div>
                    )}
                  </TableCell>
                  <TableCell>{c.device_serial || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{sharedWith(c.device_id).join(", ") || "—"}</TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    No cameras found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <p className="text-xs text-muted-foreground">
          "Public" is a setting on the camera itself (the same as Enable Public View in camera settings). It affects every
          showcase the camera appears on, including the main organisation's and other sub-customers it is shared with.
        </p>
      </CardContent>
    </Card>
  );
};

const ShowcaseTab = ({ org, canManage }: { org: SubOrganization; canManage: boolean }) => {
  const update = useUpdateSubOrganization();
  const [isPublic, setIsPublic] = useState(org.visibility === "public");
  const [codeOn, setCodeOn] = useState(!!org.showcase_access_code_enabled);
  const [code, setCode] = useState(org.showcase_access_code ?? "");
  useEffect(() => {
    setIsPublic(org.visibility === "public");
    setCodeOn(!!org.showcase_access_code_enabled);
    setCode(org.showcase_access_code ?? "");
  }, [org]);

  const url = `${window.location.origin}/showcase/${org.slug || org.id}`;
  const save = () => {
    if (codeOn && code.trim().length < 4) return toast.error("Access code must be at least 4 characters");
    update.mutate({
      id: org.id,
      data: {
        visibility: isPublic ? "public" : "private",
        showcase_access_code_enabled: codeOn,
        showcase_access_code: code.trim() || null,
      } as any,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Showcase page</CardTitle>
        <CardDescription>A public demonstration page showing this sub-customer's shared cameras.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Switch id="sc-public" checked={isPublic} disabled={!canManage || !!org.archived_at} onCheckedChange={setIsPublic} />
          <Label htmlFor="sc-public">Showcase page is public</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="sc-code" checked={codeOn} disabled={!canManage} onCheckedChange={setCodeOn} />
          <Label htmlFor="sc-code">Require an access code</Label>
        </div>
        {codeOn && (
          <Input value={code} disabled={!canManage} onChange={(e) => setCode(e.target.value)} placeholder="Access code" className="max-w-xs" maxLength={32} />
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground break-all">{url}</span>
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> Open
            </a>
          </Button>
        </div>
        <div className="space-y-2">
          <Label>QR code</Label>
          <div className="w-fit">
            <ShowcaseQRCode organizationId={org.slug || org.id} organizationName={org.name} size={180} />
          </div>
          {!isPublic && (
            <p className="text-xs text-muted-foreground">The QR code will only open the page once the showcase is public and saved.</p>
          )}
          {codeOn && (
            <p className="text-xs text-muted-foreground">Visitors will be asked for the access code.</p>
          )}
        </div>
        {canManage && (
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save showcase settings
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const SubCustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const ctx = useSubOrgContext();
  const { data: org, isLoading } = useSubOrganization(id);
  const archive = useArchiveSubOrganization();
  const [params, setParams] = useSearchParams();
  const tab = ["profile", "users", "cameras", "credits", "showcase"].includes(params.get("tab") || "") ? params.get("tab")! : "profile";
  const { data: userData } = useSubOrgUsers(id);
  const { data: devs = [] } = useSubOrgDevices(id);

  if (ctx.isLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const allowed =
    !!org?.parent_organization_id &&
    (ctx.isSystemAdmin || (ctx.isMainOrgUser && ctx.ownOrganization?.id === org.parent_organization_id));

  if (!org || !allowed) {
    return (
      <div className="customer-admin-scope flex flex-col gap-4 items-center justify-center h-screen">
        <p className="text-muted-foreground">Sub-customer not found or not available to you.</p>
        <Button variant="outline" asChild>
          <Link to="/dashboard/sub-customers">Back to sub-customers</Link>
        </Button>
      </div>
    );
  }

  const canManage = ctx.canManage;

  return (
    <SidebarProvider>
      <div className="customer-admin-scope min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <FixedPageHeader
            title={org.name}
            description={org.archived_at ? "Customer Management · archived sub-customer" : "Customer Management · sub-customer"}
            icon={Building}
            actions={
              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link to={`/dashboard/sub-customers${ctx.isSystemAdmin ? `?org=${org.parent_organization_id}` : ""}`}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Customer Management
                  </Link>
                </Button>
                {canManage && (
                  <Button
                    variant={org.archived_at ? "default" : "destructive"}
                    disabled={archive.isPending}
                    onClick={() => {
                      const a = !org.archived_at;
                      if (!a || confirm("Archive this sub-customer? Its users will lose access to its cameras and the showcase will go private."))
                        archive.mutate({ id: org.id, archive: a });
                    }}
                  >
                    {org.archived_at ? "Restore" : "Archive"}
                  </Button>
                )}
              </div>
            }
          />
          <div className="px-4 md:px-6 pb-6 pt-24 md:pt-[8.5rem]">
            <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
              <TabsList>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="users">Users ({userData?.users.length ?? 0})</TabsTrigger>
                <TabsTrigger value="cameras">Cameras ({devs.length})</TabsTrigger>
                <TabsTrigger value="credits">Credits</TabsTrigger>
                <TabsTrigger value="showcase">Showcase</TabsTrigger>
              </TabsList>
              <TabsContent value="profile"><ProfileTab org={org} canManage={canManage} /></TabsContent>
              <TabsContent value="users"><UsersTab org={org} canManage={canManage} /></TabsContent>
              <TabsContent value="cameras"><CamerasTab org={org} canManage={canManage} /></TabsContent>
              <TabsContent value="credits">
                <SubOrganizationCredits subOrganizationId={org.id} parentOrganizationId={org.parent_organization_id} />
              </TabsContent>
              <TabsContent value="showcase"><ShowcaseTab org={org} canManage={canManage} /></TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default SubCustomerDetail;
